<template>
  <div
    v-if="open"
    ref="menu"
    class="app-account"
    role="menu"
    aria-label="Account"
    data-test="app-shell-account-panel"
    @keydown="onKeydown"
  >
    <div v-if="showIdentity" class="app-account__who" data-test="app-account-identity">
      <span class="app-account__avatar" aria-hidden="true">
        <img v-if="avatarSrc" :src="avatarSrc" alt="" @error="avatarFailed = true" />
        <template v-else>{{ initials }}</template>
      </span>
      <span class="app-account__who-text">
        <span class="app-account__name">{{ accountName }}</span>
        <span v-if="roleLabel" class="app-account__role">{{ roleLabel }}</span>
      </span>
    </div>

    <div v-if="officeName" class="app-account__office" data-test="app-account-office">
      <span class="app-account__office-icon"><AppIcon name="building" :size="16" /></span>
      <span class="app-account__office-text">
        <span class="app-account__office-name">{{ officeName }}</span>
        <span v-if="officeSub" class="app-account__office-sub">{{ officeSub }}</span>
      </span>
      <button
        v-if="canSwitch"
        type="button"
        class="app-account__switch"
        role="menuitem"
        data-test="app-account-switch-site"
        @click="emitAndClose('switch-site')"
      >Switch</button>
    </div>

    <button
      v-if="canMyAccount"
      type="button"
      class="app-account__item"
      role="menuitem"
      data-test="app-account-my-account"
      @click="emitAndClose('my-account')"
    >
      <span class="app-account__item-icon"><AppIcon name="user" :size="16" /></span>
      My account
    </button>
    <button
      v-if="canSettings"
      type="button"
      class="app-account__item"
      role="menuitem"
      data-test="app-account-settings"
      @click="emitAndClose('settings')"
    >
      <span class="app-account__item-icon"><AppIcon name="gear" :size="16" /></span>
      Settings
    </button>

    <!-- Demo mask (Super Admin only, per USER — same as the classic gear menu). The click asks
         AppShell to confirm + reload (shellActions.confirmToggleMask); this switch never flips
         anything itself. -->
    <template v-if="superAdmin">
      <div class="app-account__divider" role="separator"></div>
      <button
        type="button"
        class="app-account__mask"
        role="menuitemcheckbox"
        :aria-checked="maskOn ? 'true' : 'false'"
        data-test="app-account-demo-mask"
        @click="emitAndClose('toggle-mask')"
      >
        <span class="app-account__item-icon"><AppIcon name="eyeOff" :size="16" /></span>
        <span class="app-account__mask-text">
          <span class="app-account__mask-title">Demo mask</span>
          <span class="app-account__mask-hint">{{ maskOn ? 'Patient data is faked. Sends are blocked.' : 'Fake patient data for recording' }}</span>
        </span>
        <span class="app-account__toggle" :class="{ 'is-on': maskOn }" aria-hidden="true"><span></span></span>
      </button>
    </template>

    <div class="app-account__divider" role="separator"></div>
    <button
      v-if="superAdmin"
      type="button"
      class="app-account__item"
      role="menuitem"
      data-test="app-account-classic-nav"
      @click="emitAndClose('classic-nav')"
    >
      <span class="app-account__item-icon"><AppIcon name="panelOpen" :size="16" /></span>
      Classic navigation
    </button>
    <!-- Office users (never a Super Admin): the ONE "classic Teamio" / "new Teamio" switch for every
         released area at once (uiVersion.js officeSwitchState). AppShell stores the choice and reloads. -->
    <button
      v-if="officeSwitchVisible"
      type="button"
      class="app-account__item"
      role="menuitem"
      data-test="app-account-office-switch"
      @click="emitAndClose('office-switch', officeSwitch.target)"
    >
      <span class="app-account__item-icon"><AppIcon name="panelOpen" :size="16" /></span>
      {{ officeSwitchText }}
    </button>
    <button
      type="button"
      class="app-account__item"
      role="menuitem"
      data-test="app-account-logout"
      @click="emitAndClose('logout')"
    >
      <span class="app-account__item-icon"><AppIcon name="logout" :size="16" /></span>
      Log out
    </button>
  </div>
</template>

<script>
/**
 * App shell — the account menu (design 1d): who you are, the office (with Switch), My account,
 * Settings, the Demo mask (Super Admin), Classic navigation (Super Admin), Log out. It replaces
 * the classic header's gear menu, with the same gates:
 *   My account  $can('view','myaccount')     → canMyAccount
 *   Settings    Admin or Super Admin         → canSettings
 *   Switch      more than one site           → canSwitch
 *   Demo mask / Classic navigation  Super Admin → superAdmin
 *   Switch to classic Teamio / Try the new Teamio  office users → officeSwitch { visible, target }
 *     (uiVersion.js officeSwitchState; never visible for a Super Admin). Emits office-switch(target).
 *
 * AppShell owns open/close: `open` in, `close` out (Escape, or after any item). Items only emit;
 * AppShell runs them through shellActions.js.
 *
 * Prop names avoid src/mixin.js computeds (`user`, `isSuperAdmin`) on purpose — see
 * cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import AppIcon from './AppIcon.vue';
import { officeSwitchLabel } from '@/utils/followups/uiCopy.js';

export default {
  name: 'AppAccountMenu',
  components: { AppIcon },

  props: {
    open: { type: Boolean, default: false },
    account: { type: Object, default: () => ({}) }, // { name, role, avatarUrl? }
    office: { type: Object, default: () => ({}) }, // { name, index, count }
    canMyAccount: { type: Boolean, default: false },
    canSettings: { type: Boolean, default: false },
    canSwitch: { type: Boolean, default: false },
    superAdmin: { type: Boolean, default: false },
    maskOn: { type: Boolean, default: false },
    showIdentity: { type: Boolean, default: true },
    // { visible, target } from uiVersion.js officeSwitchState — hidden unless told otherwise.
    officeSwitch: { type: Object, default: () => ({ visible: false, target: null }) },
  },

  data() {
    return { avatarFailed: false };
  },

  computed: {
    officeSwitchVisible() {
      const s = this.officeSwitch;
      return !!(s && s.visible === true && (s.target === 'old' || s.target === 'new'));
    },
    officeSwitchText() {
      return officeSwitchLabel(this.officeSwitch && this.officeSwitch.target);
    },
    accountName() {
      return (this.account && this.account.name) || '';
    },
    // "Super Admin" → "Super admin" (the design's sentence case).
    roleLabel() {
      const r = String((this.account && this.account.role) || '').trim();
      return r ? r.charAt(0).toUpperCase() + r.slice(1).toLowerCase() : '';
    },
    initials() {
      const parts = this.accountName.split(/\s+/).filter(Boolean);
      if (!parts.length) return '';
      const first = parts[0].charAt(0);
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
      return (first + last).toUpperCase();
    },
    avatarSrc() {
      return !this.avatarFailed && this.account && this.account.avatarUrl ? this.account.avatarUrl : '';
    },
    officeName() {
      return (this.office && this.office.name) || '';
    },
    officeSub() {
      const n = Number(this.office && this.office.count) || 0;
      const i = Number(this.office && this.office.index) || 0;
      if (n <= 1) return '';
      return i > 0 ? `${i} of ${n} offices` : `${n} offices`;
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen) this.$nextTick(() => this.focusItem(0));
    },
    'account.avatarUrl'() {
      this.avatarFailed = false;
    },
  },

  methods: {
    items() {
      const menu = this.$refs.menu;
      return menu ? Array.prototype.slice.call(menu.querySelectorAll('[role^="menuitem"]')) : [];
    },

    focusItem(i) {
      const list = this.items();
      if (!list.length) return;
      const k = (i + list.length) % list.length;
      if (typeof list[k].focus === 'function') list[k].focus();
    },

    onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
        this.$emit('close');
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const list = this.items();
      const at = list.indexOf(document.activeElement);
      this.focusItem(e.key === 'ArrowDown' ? at + 1 : (at < 0 ? list.length - 1 : at - 1));
    },

    emitAndClose(event, payload) {
      if (payload === undefined) this.$emit(event);
      else this.$emit(event, payload);
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-account {
  position: absolute;
  top: 56px;
  right: 16px;
  z-index: $shell-z-popover;
  width: 290px;
  max-width: calc(100vw - 24px);
  padding: 6px;
  background: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 14px;
  box-shadow: $fu-shadow-lg;
  font-family: $fu-font-sans;
  color: $fu-text;
  animation: app-account-pop 0.14s ease;
}

@keyframes app-account-pop {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

.app-account__who {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 12px;
}

.app-account__avatar {
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 50%;
  overflow: hidden;
  background: $fu-brand-50;
  color: $fu-brand-700;
  font-size: 14px;
  font-weight: $fu-fw-bold;
  display: flex;
  align-items: center;
  justify-content: center;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.app-account__who-text,
.app-account__office-text,
.app-account__mask-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.app-account__name { font-size: 14.5px; font-weight: $fu-fw-bold; @include fu-ellipsis; }
.app-account__role { font-size: 12.5px; color: $fu-neutral-500; }

.app-account__office {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 4px 6px;
  padding: 10px 12px;
  border: 1px solid $fu-border;
  border-radius: 10px;
  background: $fu-neutral-25;
}

.app-account__office-icon { display: flex; color: $fu-neutral-500; }
.app-account__office-text { flex: 1; }
.app-account__office-name { font-size: 13.5px; font-weight: $fu-fw-bold; @include fu-ellipsis; }
.app-account__office-sub { font-size: 12px; color: $fu-neutral-500; }

.app-account__switch {
  @include fu-button-reset;
  @include fu-focus-ring;
  flex: none;
  padding: 2px 4px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: $fu-fw-bold;
  color: $fu-brand-700;
}

.app-account__item {
  @include fu-button-reset;
  @include fu-focus-ring;
  @include shell-menu-row;
  width: 100%;
  text-align: left;
}

.app-account__item-icon { display: flex; color: $fu-neutral-500; }

.app-account__divider {
  height: 1px;
  margin: 6px 4px;
  background: $fu-border;
}

.app-account__mask {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  text-align: left;

  &:hover { background: $fu-neutral-50; }
}

.app-account__mask-text { flex: 1; }
.app-account__mask-title { font-size: 13.5px; font-weight: $fu-fw-semibold; }
.app-account__mask-hint { font-size: 12px; line-height: 1.35; color: $fu-neutral-500; }

// The design system's Switch (40×23), drawn — the click is the whole row.
.app-account__toggle {
  position: relative;
  width: 40px;
  height: 23px;
  flex: none;
  border-radius: 999px;
  background: $fu-neutral-300;
  transition: background $fu-dur-fast $fu-ease-out;

  span {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: #fff;
    box-shadow: $fu-shadow-sm;
    transition: transform $fu-dur-fast $fu-ease-out;
  }

  &.is-on {
    background: $fu-brand;
    span { transform: translateX(17px); }
  }
}

// Touch (1g / 1h): 44px rows.
@include shell-below-desktop {
  .app-account__item,
  .app-account__mask { min-height: $shell-touch-target; }
  .app-account__switch { min-height: $shell-touch-target; padding: 0 8px; }
}
</style>
